import { formatTeamMemberId } from '@/common/utils/formatTeamMemberId';

export type InteractionsAgent = {
  /**
   * The facet id: `legacyExternalId ?? userId`. Not stable across a person's
   * memberships — identity only backfilled `legacyExternalId` onto rows that
   * predate the migration, so one user can produce both forms. Use it to write
   * and match facets, never to identify the person.
   */
  id: string;
  /** Always the UUID, so it identifies the person across all their rows. */
  userId: string;
  firstName: string;
  lastName: string;
  teamName: string;
  teamId: string;
};

/**
 * Whether a cursor-paginated response has more pages.
 *
 * Must read `paging.next`, never `paging.cursors.after`: identity populates
 * `after` on every response including the last one (cursors are base64 offsets
 * computed unconditionally), so gating on it reports "more pages" forever.
 */
export function hasNextPage(response?: {
  paging?: { next?: string | null };
}): boolean {
  return Boolean(response?.paging?.next);
}

/**
 * Unions the sources, deduped per (agent, team) pair.
 *
 * Later groups overwrite earlier ones, so callers pass the self group **first**:
 * a permission row for the same person and team carries a real name, while the
 * self row only has the session's, and the permission row should win.
 */
export function mergeAgents(
  groups: InteractionsAgent[][],
  currentUserId: string,
): InteractionsAgent[] {
  const byKey = new Map<string, InteractionsAgent>();

  for (const group of groups) {
    for (const agent of group) {
      byKey.set(formatTeamMemberId(agent.userId, agent.teamId), agent);
    }
  }

  return sortAgents([...byKey.values()], currentUserId);
}

/** Current user first, then alphabetical by first name then last name. */
function sortAgents(
  agents: InteractionsAgent[],
  currentUserId: string,
): InteractionsAgent[] {
  return [...agents].sort((a, b) => {
    const aSelf = a.userId === currentUserId ? 0 : 1;
    const bSelf = b.userId === currentUserId ? 0 : 1;
    if (aSelf !== bSelf) return aSelf - bSelf;
    return (
      a.firstName.localeCompare(b.firstName) ||
      a.lastName.localeCompare(b.lastName)
    );
  });
}
